export function formatInsightMinutes (value) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue) || numericValue <= 0) return '00:00'

  const rounded = Math.max(0, Math.round(numericValue))
  const hours = Math.floor(rounded / 60)
  const minutes = String(rounded % 60).padStart(2, '0')
  return `${String(hours).padStart(2, '0')}:${minutes}`
}

export function formatInsightWorkDays (value) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue) || numericValue <= 0) return '0'

  return Number.isInteger(numericValue)
    ? String(numericValue)
    : numericValue.toFixed(1)
}

export function formatInsightYearMonth (value) {
  const text = String(value || '')
  if (!/^\d{6}$/.test(text)) return text
  return `${text.substring(0, 4)}年${Number(text.substring(4, 6))}月`
}

export function clampInsightPercent (value) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return 0
  return Math.min(100, Math.max(0, numericValue))
}

export function documentStatusTone (status, waitingDays = 0, applicable = true) {
  if (applicable === false) return 'excluded'
  if (String(status) === '4') return 'approved'
  if (String(status) === '3') return 'rejected'
  if (String(status) === '2' && Number(waitingDays) >= 3) return 'overdue'
  if (String(status) === '2') return 'pending'
  return 'unsubmitted'
}

/**
 * 「時:分」（「時:分:秒」も）の文字を、分へ直してから組み立て直す。
 *
 * **先頭5文字で切らない。**月の勤務時間は100時間を超えると時が3桁になり、
 * 「171:30」が「171:3」、「160:00」が「160:0」と出ていた（束35）。
 * 着地予測（formatInsightMinutes）と同じ組み立てにそろえる。
 *
 * 読めない値は、そのまま返す（00:00 と出すと、0時間と区別がつかない）。
 */
export function formatDurationText (value) {
  if (value === null || value === undefined || value === '') return '00:00'
  const text = String(value).trim()
  const matched = /^(\d+):([0-5]\d)(?::[0-5]\d(?:\.\d+)?)?$/.exec(text)
  if (!matched) return text
  const minutes = Number(matched[1]) * 60 + Number(matched[2])
  if (minutes <= 0) return '00:00'
  return formatInsightMinutes(minutes)
}

/** yyyyMM の月だけ（「9月」）。読めなければ空。 */
export function monthOnlyLabel (value) {
  const text = String(value || '')
  if (!/^\d{6}$/.test(text)) return ''
  return `${Number(text.substring(4, 6))}月`
}

/**
 * 数えた月を並べる（「9月」「9・10月」。年をまたぐ1月は「12・1月」）。
 *
 * 月の数字を「・」でつなぎ、最後に「月」を1つだけ付ける
 * （2026-10-08 に決めた形。小さい見出しの横に出すので短くする）。
 *
 * ダッシュボードの未申請は、締め中の月と前月の出し忘れを数える。
 * どの月の数字かを、数字の横に出すために使う（束18）。
 * 差戻しは月を問わず数えるので、差戻しの横には使わない。
 */
export function monthListLabel (values) {
  if (!Array.isArray(values)) return ''
  const months = values
    .map(value => String(value || ''))
    .filter(text => /^\d{6}$/.test(text))
    .map(text => String(Number(text.substring(4, 6))))
  return months.length ? months.join('・') + '月' : ''
}

/**
 * 管理者の一覧を開くときに、年月（と氏名）を渡す。
 *
 * 一覧の画面は、開いたときに「前回の検索条件」（sessionStorage）を読んで
 * その条件で検索する。そこへ年月と氏名を書いてから開く。
 * プロジェクト集計の［請求書管理へ］と同じ作り。
 */
export const LIST_SEARCH_KEYS = {
  '/admin/roster/list': 'rosterList-search-params',
  '/admin/monthlyreports/list': 'monthlyreportsList-search-params',
  '/admin/payoff/expenseList': 'expenseList-search-params',
  '/admin/payoff/travelCostList': 'travelcostList-search-params',
  '/admin/paidandsubstitute/list': 'paidandsubstituteList-search-params',
  '/admin/total/list': 'totalList-search-params',
  '/admin/total/list_project': 'project-summary-search-params',
  '/admin/sales/salesManagement': 'sales-search-params'
}

/** yyyyMM を一覧の検索条件（year・month）にする。読めなければ null。 */
export function listSearchParams (route, yearMonth, keyword) {
  const text = String(yearMonth || '')
  if (!LIST_SEARCH_KEYS[route] || !/^\d{6}$/.test(text)) return null
  const params = { year: text.substring(0, 4), month: String(Number(text.substring(4, 6))) }
  if (route === '/admin/total/list_project' || route === '/admin/sales/salesManagement') return params
  params.keyword = keyword || ''
  params.currentPage = 1
  return params
}

export function openListWith (router, route, yearMonth, keyword) {
  const params = listSearchParams(route, yearMonth, keyword)
  if (params) {
    try {
      sessionStorage.setItem(LIST_SEARCH_KEYS[route], JSON.stringify(params))
    } catch (e) {
      // 書けなくても一覧は開く（既定の月で開くだけ）。
    }
  }
  if (router) router.push(route)
}

